import { useEffect, useState } from 'react';
import { resumeService } from '../services/resume.service';
import { MessageHistoryItem, TailoredHistoryItem, Resume } from '../types';
import { Download, Copy } from 'lucide-react';

type HistoryTab = 'tailored' | 'messages' | 'shared';

export function HistoryPage() {
    const [activeTab, setActiveTab] = useState<HistoryTab>('tailored');
    const [tailoredHistory, setTailoredHistory] = useState<TailoredHistoryItem[]>([]);
    const [messageHistory, setMessageHistory] = useState<MessageHistoryItem[]>([]);
    const [resumes, setResumes] = useState<Resume[]>([]);
    const [isLoadingTailored, setIsLoadingTailored] = useState(true);
    const [isLoadingMessages, setIsLoadingMessages] = useState(true);
    const [toast, setToast] = useState('');
    const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [tailored, messages] = await Promise.all([
                    resumeService.getTailoredHistory(40),
                    resumeService.getMessageHistory(40),
                ]);
                setTailoredHistory(tailored);
                setMessageHistory(messages);
                const resumeData = await resumeService.getResumes(1, 50);
                setResumes(resumeData.resumes);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoadingTailored(false);
                setIsLoadingMessages(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(''), 2500);
        return () => clearTimeout(timer);
    }, [toast]);

    const handleShareTailored = async (item: TailoredHistoryItem) => {
        const updated = await resumeService.shareTailored(item.id);
        setTailoredHistory((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
        if (updated.public_id) {
            const url = `${window.location.origin}/t/${updated.public_id}`;
            await navigator.clipboard.writeText(url);
            setToast('Public link copied');
            setCopiedLinkId(updated.id);
            setTimeout(() => setCopiedLinkId((current) => (current === updated.id ? null : current)), 1500);
        }
    };

    const handleUnshareTailored = async (item: TailoredHistoryItem) => {
        const updated = await resumeService.unshareTailored(item.id);
        setTailoredHistory((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
        setToast('Sharing disabled');
    };

    const handleShareResume = async (item: Resume) => {
        const updated = await resumeService.shareResume(item.id);
        setResumes((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
        if (updated.public_id) {
            const url = `${window.location.origin}/r/${updated.public_id}`;
            await navigator.clipboard.writeText(url);
            setToast('Public link copied');
        }
    };

    const handleUnshareResume = async (item: Resume) => {
        const updated = await resumeService.unshareResume(item.id);
        setResumes((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
        setToast('Sharing disabled');
    };

    const handleCopyMessage = async (body: string) => {
        await navigator.clipboard.writeText(body);
        setToast('Message copied');
    };

    const handleDownloadMessage = async (item: MessageHistoryItem) => {
        const name = item.company_name?.trim() || 'application';
        const blob = new Blob([item.body], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-3xl font-semibold">History</h2>
                <p className="text-sm text-gray-500">Tailored CVs and generated messages.</p>
            </div>

            {toast && (
                <div className="text-sm px-4 py-2 bg-green-50 text-green-800 border border-green-200 rounded">
                    {toast}
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                <button
                    className={`btn ${activeTab === 'tailored' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('tailored')}
                >
                    Tailored CV history
                </button>
                <button
                    className={`btn ${activeTab === 'messages' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('messages')}
                >
                    Message history
                </button>
                <button
                    className={`btn ${activeTab === 'shared' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('shared')}
                >
                    Shared links
                </button>
            </div>

            {activeTab === 'tailored' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    {isLoadingTailored ? (
                        <p className="text-sm text-gray-500">Loading tailored history...</p>
                    ) : tailoredHistory.length ? (
                        <div className="space-y-3">
                            {tailoredHistory.map((item) => {
                                const publicUrl = item.public_id ? `${window.location.origin}/t/${item.public_id}` : '';
                                return (
                                    <div key={item.id} className="border border-gray-100 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-semibold">{item.title}</p>
                                                <p className="text-xs text-gray-500">
                                                    {item.company_name || 'Unknown company'} | {item.application_type || 'n/a'}
                                                </p>
                                            </div>
                                            <div className="text-xs text-gray-500">Views: {item.view_count ?? 0}</div>
                                        </div>
                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                            {item.is_public ? (
                                                <>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => handleUnshareTailored(item)}
                                                    >
                                                        Disable link
                                                    </button>
                                                        <button
                                                            className="btn btn-sm btn-secondary"
                                                            onClick={async () => {
                                                                if (publicUrl) {
                                                                    await navigator.clipboard.writeText(publicUrl);
                                                                    setToast('Public link copied');
                                                                    setCopiedLinkId(item.id);
                                                                    setTimeout(
                                                                        () => setCopiedLinkId((current) => (current === item.id ? null : current)),
                                                                        1500
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            {copiedLinkId === item.id ? 'Copied' : 'Copy link'}
                                                        </button>
                                                </>
                                            ) : (
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => handleShareTailored(item)}
                                                >
                                                    Share link
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No tailored history yet.</p>
                    )}
                </div>
            )}

            {activeTab === 'messages' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    {isLoadingMessages ? (
                        <p className="text-sm text-gray-500">Loading message history...</p>
                    ) : messageHistory.length ? (
                        <div className="space-y-3">
                            {messageHistory.map((item) => (
                                <div key={item.id} className="border border-gray-100 rounded-lg p-4 space-y-3">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-semibold">
                                                {item.company_name || 'Unknown company'} | {item.application_type || 'n/a'}
                                            </p>
                                            {item.subject && (
                                                <p className="text-xs text-gray-500">Subject: {item.subject}</p>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleCopyMessage(item.body)}
                                            >
                                                <Copy size={14} />
                                                Copy
                                            </button>
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleDownloadMessage(item)}
                                            >
                                                <Download size={14} />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-600 whitespace-pre-wrap">
                                        {item.body.slice(0, 300)}
                                        {item.body.length > 300 ? '...' : ''}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No messages yet.</p>
                    )}
                </div>
            )}

            {activeTab === 'shared' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3">Main resumes</h3>
                        {resumes.filter((item) => item.is_public || item.public_id).length ? (
                            <div className="space-y-3">
                                {resumes
                                    .filter((item) => item.is_public || item.public_id)
                                    .map((item) => {
                                        const publicUrl = item.public_id ? `${window.location.origin}/r/${item.public_id}` : '';
                                        return (
                                            <div key={item.id} className="border border-gray-100 rounded-lg p-4">
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <div>
                                                        <p className="text-sm font-semibold">{item.title}</p>
                                                        <p className="text-xs text-gray-500">Views: {item.view_count ?? 0}</p>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {item.is_public ? (
                                                            <>
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={() => handleUnshareResume(item)}
                                                                >
                                                                    Disable link
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={async () => {
                                                                        if (publicUrl) {
                                                                            await navigator.clipboard.writeText(publicUrl);
                                                                            setToast('Public link copied');
                                                                        }
                                                                    }}
                                                                >
                                                                    Copy link
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() => handleShareResume(item)}
                                                            >
                                                                Share link
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">No shared resumes yet.</p>
                        )}
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold mb-3">Tailored CVs</h3>
                        {tailoredHistory.filter((item) => item.is_public || item.public_id).length ? (
                            <div className="space-y-3">
                                {tailoredHistory
                                    .filter((item) => item.is_public || item.public_id)
                                    .map((item) => {
                                        const publicUrl = item.public_id ? `${window.location.origin}/t/${item.public_id}` : '';
                                        return (
                                            <div key={item.id} className="border border-gray-100 rounded-lg p-4">
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <div>
                                                        <p className="text-sm font-semibold">{item.title}</p>
                                                        <p className="text-xs text-gray-500">Views: {item.view_count ?? 0}</p>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {item.is_public ? (
                                                            <>
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={() => handleUnshareTailored(item)}
                                                                >
                                                                    Disable link
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={async () => {
                                                                        if (publicUrl) {
                                                                            await navigator.clipboard.writeText(publicUrl);
                                                                            setToast('Public link copied');
                                                                        }
                                                                    }}
                                                                >
                                                                    Copy link
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() => handleShareTailored(item)}
                                                            >
                                                                Share link
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">No shared tailored CVs yet.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
